import { Position } from '../types';
import type { XYPosition } from '../types';

const HALF = 0.5;

/** Unit vector pointing out of a node through the side a handle sits on. */
const OUTWARD: Record<Position, XYPosition> = {
  [Position.Left]: { x: -1, y: 0 },
  [Position.Right]: { x: 1, y: 0 },
  [Position.Top]: { x: 0, y: -1 },
  [Position.Bottom]: { x: 0, y: 1 },
};

const isHorizontal = (position: Position): boolean => position === Position.Left || position === Position.Right;
const transpose = ({ x, y }: XYPosition): XYPosition => ({ x: y, y: x });
const reverse = ({ x, y }: XYPosition): XYPosition => ({ x: -x, y: -y });
const movesAlong = (from: XYPosition, to: XYPosition, direction: XYPosition): boolean =>
  (to.x - from.x) * direction.x + (to.y - from.y) * direction.y >= 0;

/**
 * Bends between two horizontal ends `start` → `end` (`out` / `into`: their outward directions). Facing each other
 * with room: one vertical jog in the middle. Facing the same way: go out to the further end. Otherwise: cross over
 * through the row halfway between them.
 */
const parallelBends = (start: XYPosition, out: XYPosition, end: XYPosition, into: XYPosition): XYPosition[] => {
  const hasRoom = (end.x - start.x) * out.x > 0;
  if (out.x === -into.x && hasRoom) {
    const middleX = (start.x + end.x) * HALF;
    return [{ x: middleX, y: start.y }, { x: middleX, y: end.y }];
  }
  if (out.x === into.x) {
    const outerX = out.x > 0 ? Math.max(start.x, end.x) : Math.min(start.x, end.x);
    return [{ x: outerX, y: start.y }, { x: outerX, y: end.y }];
  }
  const middleY = (start.y + end.y) * HALF;
  return [{ x: start.x, y: middleY }, { x: end.x, y: middleY }];
};

/** One bend between a horizontal and a vertical end: keep going the start's way, then turn into the end. */
const crossBend = (start: XYPosition, out: XYPosition, end: XYPosition, into: XYPosition): XYPosition[] => {
  const startIsHorizontal = out.y === 0;
  const turnLate = startIsHorizontal ? { x: end.x, y: start.y } : { x: start.x, y: end.y };
  if (movesAlong(start, turnLate, out) && movesAlong(turnLate, end, reverse(into))) return [turnLate];
  return [startIsHorizontal ? { x: start.x, y: end.y } : { x: end.x, y: start.y }];
};

/** Drops repeated points and points in the middle of a straight run, so every inner point is a real corner. */
const toCorners = (points: readonly XYPosition[]): XYPosition[] =>
  points.reduce<XYPosition[]>((corners, point) => {
    if (corners.length === 0) return [point];
    const last = corners[corners.length - 1];
    if (last.x === point.x && last.y === point.y) return corners;
    if (corners.length === 1) return [...corners, point];
    const beforeLast = corners[corners.length - 2];
    const isStraight = (last.x - beforeLast.x) * (point.y - last.y) === (last.y - beforeLast.y) * (point.x - last.x);
    return isStraight ? [...corners.slice(0, -1), point] : [...corners, point];
  }, []);

/**
 * Right-angled route from a source handle to a target handle: leave and enter each handle straight for `offset`,
 * joined by one or two bends. Returns the corner points including both ends.
 */
export const getStepRoute = (
  source: XYPosition,
  sourcePosition: Position,
  target: XYPosition,
  targetPosition: Position,
  offset: number,
): XYPosition[] => {
  const out = OUTWARD[sourcePosition];
  const into = OUTWARD[targetPosition];
  const start = { x: source.x + out.x * offset, y: source.y + out.y * offset };
  const end = { x: target.x + into.x * offset, y: target.y + into.y * offset };
  let bends: XYPosition[];
  if (isHorizontal(sourcePosition) !== isHorizontal(targetPosition)) bends = crossBend(start, out, end, into);
  else if (isHorizontal(sourcePosition)) bends = parallelBends(start, out, end, into);
  else bends = parallelBends(transpose(start), transpose(out), transpose(end), transpose(into)).map(transpose);
  return toCorners([source, start, ...bends, end, target]);
};
